import { useNavigate, useLocation } from "react-router-dom";
import { offsetShadow, TYPE, useMe } from "@chess/shared";
import { useTheme } from "@/theme/ThemeProvider";
import { useViewport } from "@/util/viewport";
import { Logo } from "./Logo";

interface Props {
  onOpenSettings: () => void;
}

interface NavLink {
  to: string;
  label: string;
  match: (path: string) => boolean;
  adminOnly?: boolean;
}

const LINKS: NavLink[] = [
  { to: "/app", label: "Home", match: (path: string) => path === "/app" || path === "/app/" },
  { to: "/app/train", label: "Train", match: (path: string) => path.startsWith("/app/train") },
  { to: "/app/profile", label: "Profile", match: (path: string) => path.startsWith("/app/profile") || path.startsWith("/app/analysis") },
  { to: "/app/admin", label: "Admin", match: (path: string) => path.startsWith("/app/admin"), adminOnly: true },
];

export function AppNav({ onOpenSettings }: Props) {
  const p = useTheme();
  const { isMobile } = useViewport();
  const nav = useNavigate();
  const loc = useLocation();
  const { data: me } = useMe();

  const initials =
    (me?.display_name ?? me?.email ?? "??")
      .split(/\s+|@/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? "")
      .join("") || "??";

  return (
    <nav
      style={{
        height: isMobile ? 60 : 70,
        padding: isMobile ? "0 16px" : "0 32px",
        background: p.bg,
        borderBottom: `2px solid ${p.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        position: "sticky",
        top: 0,
        zIndex: 60,
        transition: "background .25s, border-color .25s",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 14 : 28 }}>
        <button
          onClick={() => nav("/app")}
          style={{
            background: "transparent",
            border: "none",
            color: p.fg,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: isMobile ? 18 : 22,
            fontWeight: 800,
            letterSpacing: -0.5,
            fontFamily: TYPE.display,
          }}
        >
          <Logo size={isMobile ? 30 : 36} />
          GAMBIT.
        </button>
        {!isMobile && (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {LINKS.filter((l) => !l.adminOnly || me?.is_admin).map((l) => {
              const active = l.match(loc.pathname);
              return (
                <button
                  key={l.to}
                  onClick={() => nav(l.to)}
                  style={{
                    background: active ? p.fg : "transparent",
                    color: active ? p.bg : p.fg,
                    border: `2px solid ${active ? p.border : "transparent"}`,
                    padding: "7px 14px",
                    fontSize: 12.5,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: 1,
                    cursor: "pointer",
                    fontFamily: TYPE.body,
                    boxShadow: active ? offsetShadow(p, 3, 3) : "none",
                  }}
                >
                  {l.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 8 : 12 }}>
        {!isMobile && (
          <div
            style={{
              height: 40,
              display: "inline-flex",
              alignItems: "center",
              padding: "0 12px",
              background: p.success,
              color: p.successFg,
              border: `2px solid ${p.border}`,
              fontFamily: TYPE.mono,
              fontWeight: 700,
              fontSize: 12,
              textTransform: "uppercase",
              letterSpacing: 0.8,
            }}
          >
            ★ {me?.streak ?? 0}d
          </div>
        )}
        <button
          onClick={onOpenSettings}
          style={{
            width: isMobile ? 36 : 40,
            height: isMobile ? 36 : 40,
            background: p.accent,
            color: p.accentFg,
            border: `2px solid ${p.border}`,
            boxShadow: offsetShadow(p, 2, 2),
            fontSize: 12,
            fontWeight: 800,
            cursor: "pointer",
            fontFamily: TYPE.body,
          }}
        >
          {initials}
        </button>
      </div>
    </nav>
  );
}
